import { z } from 'zod';

import {
  apenasDigitos,
  cnsValido,
  cpfValido,
  mascararCns,
  mascararCpf,
  mascararTelefone,
  paraAsciiMaiusculo,
} from '@/lib/mascaras';
import { PRIORIDADES, TIPOS_SOLICITACAO } from './status';
import type { PerfilUsuario } from '@/features/auth/tipos';
import { ehDemandaReprimida, escolheUbsDeOrigem, type OrigemSolicitacao } from './origem';

/**
 * Esquema do formulário de solicitação.
 *
 * Espelha `backend/src/solicitacoes/dto/solicitacao.dto.ts`. Validar no
 * cliente evita uma ida ao servidor para erro óbvio; quem decide é o backend.
 */

const opcional = (esquema: z.ZodString) =>
  z
    .string()
    .optional()
    .transform((valor) => (valor?.trim() ? valor.trim() : undefined))
    .pipe(esquema.optional());

const IDADE_MAXIMA_ANOS = 130;

export const esquemaSolicitacao = z.object({
  // --- Paciente ---
  pacienteNome: z
    .string()
    .min(3, 'Informe o nome completo do paciente.')
    .max(200, 'Nome muito longo.')
    .refine((nome) => nome.trim().includes(' '), 'Informe nome e sobrenome.'),

  pacienteDataNascimento: z
    .string()
    .min(1, 'Informe a data de nascimento.')
    .refine((valor) => {
      const data = new Date(valor);
      if (Number.isNaN(data.getTime())) return false;

      const limite = new Date();
      limite.setFullYear(limite.getFullYear() - IDADE_MAXIMA_ANOS);
      return data <= new Date() && data >= limite;
    }, 'Data inválida: não pode ser futura nem anterior a 130 anos atrás.'),

  pacienteCpf: z
    .string()
    .optional()
    .refine((valor) => !valor?.trim() || cpfValido(valor), 'CPF inválido.'),

  pacienteCns: z
    .string()
    .optional()
    .refine((valor) => !valor?.trim() || cnsValido(valor), 'Cartão Nacional de Saúde inválido.'),

  // Obrigatório: é por ele que o paciente é avisado do agendamento. E é
  // deliberadamente NÃO preenchido pela consulta ao PEC — o operador confirma
  // o número com o paciente e digita.
  pacienteTelefone: z
    .string()
    .min(1, 'Informe o telefone do paciente.')
    .refine(
      (valor) => [10, 11].includes(apenasDigitos(valor).length),
      'Telefone inválido: informe DDD e número.',
    ),

  pacienteEmail: z
    .string()
    .optional()
    .refine(
      (valor) => !valor?.trim() || z.string().email().safeParse(valor.trim()).success,
      'E-mail inválido.',
    ),

  pacienteEndereco: opcional(z.string().max(300)),
  pacienteNomeMae: opcional(z.string().max(200)),

  // --- Solicitação ---
  tipo: z.enum(TIPOS_SOLICITACAO, { message: 'Selecione o tipo da solicitação.' }),
  especialidadeId: z.string().min(1, 'Selecione a especialidade.'),
  procedimento: opcional(z.string().max(200)),
  prioridade: z.enum(PRIORIDADES).default('ELETIVA'),
  observacoes: opcional(z.string().max(2000)),

  // --- Só na demanda reprimida ---
  // Opcionais aqui e obrigatórios lá, em vez de dois esquemas sem relação:
  // manter o mesmo tipo de entrada é o que permite ao formulário da UBS e ao da
  // demanda reprimida serem o mesmo componente, sem genérico no react-hook-form.
  ubsId: z.string().optional(),
  pedidoOriginalEm: z
    .string()
    .optional()
    .refine(
      (valor) => !valor?.trim() || valor <= hojeIso(),
      'A data do pedido não pode ser futura.',
    ),
});

/**
 * O dia de hoje como string ISO.
 *
 * Comparação de texto, e não de `Date`: `new Date('2026-09-15')` é meia-noite
 * em Greenwich, que no fuso daqui ainda é dia 14 — e a validação recusaria uma
 * data legítima digitada no fim da tarde.
 */
export function hojeIso(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

/** A UBS de origem é escolhida no formulário — quem registra não tem vínculo. */
export const esquemaDemandaReprimida = esquemaSolicitacao.extend({
  ubsId: z
    .string()
    .optional()
    .refine((valor) => Boolean(valor?.trim()), 'Selecione a UBS de origem.'),
});

/** O gestor escolhe a UBS também no balcão — ele não tem unidade própria. */
export const esquemaSolicitacaoComUbs = esquemaSolicitacao.extend({
  ubsId: esquemaDemandaReprimida.shape.ubsId,
});

export function esquemaDaOrigem(origem: OrigemSolicitacao, perfil?: PerfilUsuario) {
  if (ehDemandaReprimida(origem)) return esquemaDemandaReprimida;
  return escolheUbsDeOrigem(origem, perfil) ? esquemaSolicitacaoComUbs : esquemaSolicitacao;
}

export type FormularioSolicitacao = z.input<typeof esquemaSolicitacao>;

/** Campos exibidos em cada etapa, para validar por etapa antes de avançar. */
export const CAMPOS_POR_ETAPA = {
  paciente: [
    'pacienteCpf',
    'pacienteCns',
    'pacienteNome',
    'pacienteDataNascimento',
    'pacienteTelefone',
    'pacienteEmail',
    'pacienteEndereco',
    'pacienteNomeMae',
  ],
  solicitacao: [
    'ubsId',
    'pedidoOriginalEm',
    'tipo',
    'especialidadeId',
    'procedimento',
    'prioridade',
    'observacoes',
  ],
} as const satisfies Record<string, readonly (keyof FormularioSolicitacao)[]>;

/** Remove máscaras e campos vazios antes de enviar à API. */
export function prepararEnvio(
  dados: FormularioSolicitacao,
  origem: OrigemSolicitacao = 'UBS',
  perfil?: PerfilUsuario,
) {
  const limpar = (valor?: string) => (valor?.trim() ? valor.trim() : undefined);

  const normalizar = (valor?: string) => {
    const normalizado = valor ? paraAsciiMaiusculo(valor).trim() : '';
    return normalizado || undefined;
  };

  return {
    pacienteNome: paraAsciiMaiusculo(dados.pacienteNome).trim(),
    pacienteDataNascimento: dados.pacienteDataNascimento,
    pacienteCpf: dados.pacienteCpf?.trim() ? apenasDigitos(dados.pacienteCpf) : undefined,
    pacienteCns: dados.pacienteCns?.trim() ? apenasDigitos(dados.pacienteCns) : undefined,
    pacienteTelefone: apenasDigitos(dados.pacienteTelefone),
    pacienteEmail: dados.pacienteEmail?.trim()
      ? dados.pacienteEmail.trim().toLowerCase()
      : undefined,
    // Normalizados no cliente e revalidados no servidor.
    pacienteEndereco: normalizar(dados.pacienteEndereco),
    pacienteNomeMae: normalizar(dados.pacienteNomeMae),
    tipo: dados.tipo,
    especialidadeId: dados.especialidadeId,
    procedimento: limpar(dados.procedimento),
    prioridade: dados.prioridade ?? 'ELETIVA',
    observacoes: limpar(dados.observacoes),
    // A UBS de origem vai quando o formulário a perguntou: na demanda reprimida
    // e no balcão do gestor. Para o perfil UBS a unidade vem do vínculo.
    ...(escolheUbsDeOrigem(origem, perfil) ? { ubsId: limpar(dados.ubsId) } : {}),
    // A data do pedido original só existe na demanda reprimida.
    ...(ehDemandaReprimida(origem) ? { pedidoOriginalEm: limpar(dados.pedidoOriginalEm) } : {}),
  };
}

/**
 * Preenche o formulário de edição com o que está gravado.
 *
 * Os documentos e o telefone voltam mascarados porque é assim que o campo os
 * exibe enquanto se digita — mostrar `81990001111` no lugar de
 * `(81) 99000-1111` faria o formulário parecer sujo justamente na tela em que
 * alguém abriu para conferir o número.
 */
export function valoresDoRegistro(solicitacao: {
  pacienteNome: string;
  pacienteDataNascimento: string;
  pacienteCpf: string | null;
  pacienteCns: string | null;
  pacienteTelefone: string | null;
  pacienteEmail: string | null;
  pacienteEndereco: string | null;
  pacienteNomeMae: string | null;
  tipo: FormularioSolicitacao['tipo'];
  especialidade: { id: string };
  procedimento: string | null;
  prioridade: FormularioSolicitacao['prioridade'];
  observacoes: string | null;
  /** Só existe na demanda reprimida; nulo quando não foi informada. */
  pedidoOriginalEm?: string | null;
  ubs: { id: string };
}): FormularioSolicitacao {
  return {
    pacienteNome: solicitacao.pacienteNome,
    // A coluna é do tipo data e chega como ISO completo; o campo do formulário
    // aceita apenas AAAA-MM-DD.
    pacienteDataNascimento: solicitacao.pacienteDataNascimento.slice(0, 10),
    pacienteCpf: solicitacao.pacienteCpf ? mascararCpf(solicitacao.pacienteCpf) : '',
    pacienteCns: solicitacao.pacienteCns ? mascararCns(solicitacao.pacienteCns) : '',
    pacienteTelefone: solicitacao.pacienteTelefone
      ? mascararTelefone(solicitacao.pacienteTelefone)
      : '',
    pacienteEmail: solicitacao.pacienteEmail ?? '',
    pacienteEndereco: solicitacao.pacienteEndereco ?? '',
    pacienteNomeMae: solicitacao.pacienteNomeMae ?? '',
    tipo: solicitacao.tipo,
    especialidadeId: solicitacao.especialidade.id,
    procedimento: solicitacao.procedimento ?? '',
    prioridade: solicitacao.prioridade,
    observacoes: solicitacao.observacoes ?? '',
    // Mesmo cuidado da data de nascimento: a coluna é do tipo data e volta
    // como ISO completo, e o campo do formulário só aceita AAAA-MM-DD.
    pedidoOriginalEm: solicitacao.pedidoOriginalEm?.slice(0, 10) ?? '',
    ubsId: solicitacao.ubs.id,
  };
}

/**
 * Monta o corpo do PATCH de edição.
 *
 * Difere de `prepararEnvio` num ponto que muda o resultado: campo apagado vira
 * `null`, e não `undefined`. Na criação os dois significariam a mesma coisa —
 * "não informado" —, mas num PATCH `undefined` é "não mexer", e enviar isso
 * deixaria para sempre no registro o CPF que alguém acabou de limpar da tela.
 *
 * O formulário inteiro vai junto; quem compara com o gravado e decide o que
 * mudou é o backend.
 */
export function prepararEdicao(dados: FormularioSolicitacao, origem: OrigemSolicitacao = 'UBS') {
  const limpar = (valor?: string) => (valor?.trim() ? valor.trim() : null);

  const normalizar = (valor?: string) => {
    const normalizado = valor ? paraAsciiMaiusculo(valor).trim() : '';
    return normalizado || null;
  };

  return {
    pacienteNome: paraAsciiMaiusculo(dados.pacienteNome).trim(),
    pacienteDataNascimento: dados.pacienteDataNascimento,
    pacienteCpf: dados.pacienteCpf?.trim() ? apenasDigitos(dados.pacienteCpf) : null,
    pacienteCns: dados.pacienteCns?.trim() ? apenasDigitos(dados.pacienteCns) : null,
    pacienteTelefone: apenasDigitos(dados.pacienteTelefone),
    pacienteEmail: dados.pacienteEmail?.trim() ? dados.pacienteEmail.trim().toLowerCase() : null,
    pacienteEndereco: normalizar(dados.pacienteEndereco),
    pacienteNomeMae: normalizar(dados.pacienteNomeMae),
    tipo: dados.tipo,
    especialidadeId: dados.especialidadeId,
    procedimento: limpar(dados.procedimento),
    prioridade: dados.prioridade ?? 'ELETIVA',
    observacoes: limpar(dados.observacoes),
    // Fora da demanda reprimida os dois campos nem são enviados: lá não
    // significam nada, e o servidor recusa com 400 em vez de ignorar em
    // silêncio. A unidade não aceita remoção — sem ela não há pedido —, então
    // vai só quando tem valor.
    ...(ehDemandaReprimida(origem)
      ? {
          pedidoOriginalEm: limpar(dados.pedidoOriginalEm),
          ...(dados.ubsId?.trim() ? { ubsId: dados.ubsId.trim() } : {}),
        }
      : {}),
  };
}
